import { DownOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Space, Typography } from 'antd';
import { getAccessDisplay } from '../hooks/getAccessDisplaye';
import { IoEyeSharp } from 'react-icons/io5';
import { FaStar } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export default function RendererFunctions({
    updatePermission,
    permissions,
    getAllowedAccessLevels,
}) {
    const { t } = useTranslation();
    const renderPermissionItem = (
        permission: any,
        isChild = false,
        isHeader = false,
        parentCheckboxStyle: 'check' | 'dash' = 'check'
    ) => {
        if (!permission) return null;

        const accessDisplay = getAccessDisplay(permission.access);
        const isChecked = permission.access !== 'none';

        if (isHeader) {
            return (
                <div className="flex flex-col sm:flex-row  justify-between w-full items-start sm:items-center min-h-16 gap-4 sm:gap-0 sm:px-4 px-2 py-1">
                    <div className="flex items-center space-x-3">
                        <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                                const newAccess = e.target.checked
                                    ? 'viewer'
                                    : 'none';
                                updatePermission(
                                    permission.key,
                                    newAccess,
                                    false
                                );
                            }}
                            className={`flex-shrink-0 ${
                                parentCheckboxStyle === 'dash'
                                    ? 'dash-checkbox'
                                    : ''
                            }`}
                        />
                        <div
                            className={`${isChecked ? 'text-primary' : 'text-gray-400'}`}
                        >
                            {permission.icon}
                        </div>
                        <Text
                            className={`text-base font-normal ${
                                isChecked ? 'text-primary' : 'text-gray-600'
                            }`}
                        >
                            {permission.name}
                        </Text>
                    </div>

                    <div className="w-full sm:w-auto flex justify-start sm:justify-end">
                        {permission.access === 'none' ? (
                            <Text
                                type="secondary"
                                className="text-base font-medium px-3 py-1 bg-gray-100 rounded-md"
                            >
                                {t('pages.role.form.permissions.noAccess')}
                            </Text>
                        ) : (
                            <Dropdown
                                menu={getAccessMenu(permission.key, false)}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ color: accessDisplay.color }}
                                    className="flex items-center space-x-1 px-3 py-4 rounded-md bg-blue-100"
                                >
                                    <Space size={4}>
                                        {accessDisplay.icon && (
                                            <accessDisplay.icon
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                        <span className="text-base font-medium">
                                            {accessDisplay.text}
                                        </span>
                                        <DownOutlined
                                            style={{ fontSize: '10px' }}
                                        />
                                    </Space>
                                </Button>
                            </Dropdown>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={permission.key}
                className={`flex flex-col sm:flex-row justify-between items-start sm:items-center py-3 px-6 border-b border-gray-100 last:border-b-0 gap-4 sm:gap-0 ${
                    isChecked ? 'bg-infoContainerLight' : 'bg-background'
                }`}
            >
                <div className="flex items-center space-x-3">
                    <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                            const newAccess = e.target.checked
                                ? 'viewer'
                                : 'none';
                            updatePermission(
                                permission.key,
                                newAccess,
                                isChild
                            );
                        }}
                        className="flex-shrink-0"
                    />
                    <Text
                        className={`text-base font-normal ${
                            isChecked
                                ? 'text-primary font-medium'
                                : 'text-gray-600'
                        }`}
                    >
                        {permission.name}
                    </Text>
                </div>

                <div className="w-full sm:w-auto flex justify-start sm:justify-end pl-6 sm:pl-0">
                    {permission.access === 'none' ? (
                        <Text
                            type="secondary"
                            className="text-base font-medium px-3 py-1 bg-gray-100 rounded-md"
                        >
                            {t('pages.role.form.permissions.noAccess')}
                        </Text>
                    ) : (
                        <Dropdown
                            menu={getAccessMenu(permission.key, isChild)}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                style={{ color: accessDisplay.color }}
                                className="flex items-center space-x-1 px-3 py-4 rounded-md bg-blue-50"
                            >
                                <Space size={4}>
                                    {accessDisplay.icon && (
                                        <accessDisplay.icon
                                            width={16}
                                            height={16}
                                        />
                                    )}
                                    <span className="text-base font-medium">
                                        {accessDisplay.text}
                                    </span>
                                    <DownOutlined
                                        style={{ fontSize: '10px' }}
                                    />
                                </Space>
                            </Button>
                        </Dropdown>
                    )}
                </div>
            </div>
        );
    };

    const getAccessMenu = (permissionKey: string, isChild = false) => {
        let parentAccess;
        if (isChild && permissionKey === 'ActivityLogManagement') {
            const userManagementPermission = permissions.find(
                (p) => p.key === 'Parent-UserManagement'
            );
            parentAccess = userManagementPermission?.access;
        }

        const allowedLevels = getAllowedAccessLevels(
            permissionKey,
            isChild,
            parentAccess
        );

        const allItems = [
            {
                key: 'viewer',
                label: (
                    <Space>
                        <IoEyeSharp size={16} />
                        Viewer
                    </Space>
                ),
                onClick: () =>
                    updatePermission(permissionKey, 'viewer', isChild),
            },
            {
                key: 'admin',
                label: (
                    <Space>
                        <FaStar size={16} />
                        Admin
                    </Space>
                ),
                onClick: () =>
                    updatePermission(permissionKey, 'admin', isChild),
            },
            {
                key: 'none',
                label: 'No Access',
                onClick: () => updatePermission(permissionKey, 'none', isChild),
            },
        ];

        return {
            items: allItems.filter((item) => allowedLevels.includes(item.key)),
        };
    };

    return { getAccessMenu, renderPermissionItem };
}
